import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Spinner, Dropdown, Badge } from 'react-bootstrap';
import { workoutAPI } from '../services/workoutAPI';
import RescheduleWorkoutModal from './RescheduleWorkoutModal';

function WorkoutCalendar({ activePrograms, onClose }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [workouts, setWorkouts] = useState([]);
  const [selectedProgramFilter, setSelectedProgramFilter] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);

  // Generate calendar dates for current month
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  // Get start date (Sunday of week containing first day)
  const calendarStart = new Date(firstDay);
  calendarStart.setDate(calendarStart.getDate() - calendarStart.getDay());

  // Get end date (Saturday of week containing last day)
  const calendarEnd = new Date(lastDay);
  calendarEnd.setDate(calendarEnd.getDate() + (6 - calendarEnd.getDay()));

  useEffect(() => {
    fetchCalendarData();
  }, [currentDate, selectedProgramFilter]);

  const fetchCalendarData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const filters = {
        start_date: calendarStart.toISOString().split('T')[0],
        end_date: calendarEnd.toISOString().split('T')[0]
      };

      if (selectedProgramFilter) {
        filters.active_program_id = selectedProgramFilter;
      }

      const data = await workoutAPI.fetchCalendarData(filters);
      setWorkouts(data);
    } catch (err) {
      console.error('Error fetching calendar data:', err);
      setError('Failed to load calendar data');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleWorkoutClick = (workout) => {
    setSelectedWorkout(workout);
    setShowRescheduleModal(true);
  };

  const handleRescheduleSave = async () => {
    await fetchCalendarData(); // Refresh calendar
    setShowRescheduleModal(false);
    setSelectedWorkout(null);
  };

  // Group workouts by date
  const workoutsByDate = workouts.reduce((acc, workout) => {
    if (!acc[workout.scheduled_date]) {
      acc[workout.scheduled_date] = [];
    }
    acc[workout.scheduled_date].push(workout);
    return acc;
  }, {});

  // Generate calendar grid (6 weeks x 7 days)
  const calendarGrid = [];
  const currentCalendarDate = new Date(calendarStart);

  for (let week = 0; week < 6; week++) {
    const weekRow = [];
    for (let day = 0; day < 7; day++) {
      const dateKey = currentCalendarDate.toISOString().split('T')[0];
      const dayWorkouts = workoutsByDate[dateKey] || [];
      const isCurrentMonth = currentCalendarDate.getMonth() === month;
      const isToday = dateKey === new Date().toISOString().split('T')[0];

      weekRow.push({
        date: new Date(currentCalendarDate),
        dateKey,
        dayWorkouts,
        isCurrentMonth,
        isToday
      });

      currentCalendarDate.setDate(currentCalendarDate.getDate() + 1);
    }
    calendarGrid.push(weekRow);
  }

  // Color mapping for programs
  const programColors = [
    '#9b59b6', '#3498db', '#e74c3c', '#2ecc71', '#f39c12',
    '#1abc9c', '#e67e22', '#95a5a6', '#d35400', '#c0392b'
  ];

  // Create stable color mapping: active_program_id -> color
  const programColorMap = React.useMemo(() => {
    const map = {};
    activePrograms.forEach((ap, idx) => {
      map[ap.id] = programColors[idx % programColors.length];
    });
    return map;
  }, [activePrograms]);

  const getProgramColor = (activeProgramId) => {
    return programColorMap[activeProgramId] || programColors[0];
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading calendar...</p>
      </div>
    );
  }

  return (
    <div className="workout-calendar">
      {/* Header with navigation and filters */}
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div>
            <Button variant="outline-secondary" size="sm" onClick={onClose} className="me-3">
              <i className="fas fa-arrow-left me-1"></i>
              Back
            </Button>
            <h5 className="d-inline mb-0">
              <i className="fas fa-calendar-alt me-2"></i>
              Workout Calendar
            </h5>
          </div>
          <Dropdown>
            <Dropdown.Toggle variant="outline-secondary" size="sm">
              {selectedProgramFilter
                ? activePrograms.find(ap => ap.id === selectedProgramFilter)?.program_name
                : 'All Programs'}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setSelectedProgramFilter(null)}>
                All Programs
              </Dropdown.Item>
              <Dropdown.Divider />
              {activePrograms.map(ap => (
                <Dropdown.Item
                  key={ap.id}
                  onClick={() => setSelectedProgramFilter(ap.id)}
                  active={selectedProgramFilter === ap.id}
                >
                  {ap.program_name}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </Card.Header>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* Month navigation */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Button variant="outline-secondary" size="sm" onClick={handlePrevMonth}>
          <i className="fas fa-chevron-left"></i>
        </Button>
        <h4 className="mb-0">
          {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </h4>
        <div>
          <Button variant="outline-primary" size="sm" onClick={handleToday} className="me-2">
            Today
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={handleNextMonth}>
            <i className="fas fa-chevron-right"></i>
          </Button>
        </div>
      </div>

      {/* Calendar grid */}
      <div className="calendar-grid">
        {/* Day headers */}
        <div className="calendar-day-headers">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="calendar-day-header">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar cells */}
        <div className="calendar-weeks">
          {calendarGrid.map((week, weekIdx) => (
            <div key={weekIdx} className="calendar-week">
              {week.map((cell) => (
                <div
                  key={cell.dateKey}
                  className={`calendar-cell ${
                    !cell.isCurrentMonth ? 'other-month' : ''
                  } ${cell.isToday ? 'today' : ''}`}
                >
                  <div className="calendar-cell-date">
                    {cell.date.getDate()}
                  </div>
                  <div className="calendar-cell-workouts">
                    {cell.dayWorkouts.map((workout, idx) => (
                      <div
                        key={`${workout.program_workout_id}-${workout.active_program_id}`}
                        className="workout-pill"
                        style={{
                          borderLeftColor: getProgramColor(workout.active_program_id),
                          backgroundColor: `${getProgramColor(workout.active_program_id)}15`,
                          opacity: workout.is_completed === 1 ? 0.6 : 1
                        }}
                        onClick={() => handleWorkoutClick(workout)}
                        title={`${workout.program_name}: ${workout.workout_name}`}
                      >
                        {workout.is_completed === 1 && (
                          <i className="fas fa-check-circle me-1 text-success"></i>
                        )}
                        {workout.has_override === 1 && (
                          <i className="fas fa-edit me-1 text-warning" title="Rescheduled"></i>
                        )}
                        <span className="workout-pill-text">{workout.workout_name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <Card className="mt-3">
        <Card.Body>
          <h6 className="mb-2">Program Colors:</h6>
          <div className="d-flex flex-wrap gap-2">
            {activePrograms.map((ap, idx) => (
              <span
                key={ap.id}
                className="badge"
                style={{
                  backgroundColor: programColors[idx % programColors.length],
                  color: '#fff',
                  padding: '0.35em 0.65em',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  borderRadius: '0.25rem'
                }}
              >
                {ap.program_name}
              </span>
            ))}
          </div>
        </Card.Body>
      </Card>

      {/* Reschedule Modal */}
      {showRescheduleModal && selectedWorkout && (
        <RescheduleWorkoutModal
          workout={selectedWorkout}
          onClose={() => {
            setShowRescheduleModal(false);
            setSelectedWorkout(null);
          }}
          onSave={handleRescheduleSave}
        />
      )}
    </div>
  );
}

export default WorkoutCalendar;
